// Marco de las pantallas de acceso (/authentication, /recuperar-clave,
// /nueva-clave).
//
// #1439 (mockup aprobado tools/mockups/smartdx_mockups_navbar_acceso_e_v2.html,
// "Acceso 2"): la pantalla entera es la tarjeta de "foto" del estilo E —
// fondo oscuro, foco teal, grano — con la animación de los 3 capítulos
// (AuthStory) y el formulario en un panel de vidrio. En el celular la
// animación va arriba y el vidrio sube encima al hacer scroll.
//
// SIEMPRE OSCURA, también en modo claro, a propósito (es la tarjeta de foto de
// la landing): todo el marco va en AlcanceOscuro (components/AlcanceOscuro.jsx).
// Un portal (Dialog) se monta FUERA de esa raíz y perdería las vars: por eso el
// modal de Términos usa `container={authModalRootEl}` (AuthenticationForm.js).
//
// El `pt` de la columna del formulario NO es decorativo: el navbar es
// `position:fixed` (~60px) y sin ese aire tapa el encabezado del formulario.
import React from "react";
import { Box, useMediaQuery } from "@mui/material";

import AlcanceOscuro from "../../components/AlcanceOscuro";
import AuthStory from "./AuthStory";
import { GRANO, foco } from "../../components/onboarding/fondoMedia";

export const AUTH_SCOPE_ID = "auth-scope";
// Raíz de los modales (Términos): oscura como el marco pero HERMANA de él, con
// su propio z-index sobre el navbar (1100/1500). Si el modal se montara dentro
// de #auth-scope (position:fixed = contexto de apilamiento propio) quedaba
// debajo del navbar y la píldora seguía clicable encima (revisor #1439).
export const AUTH_MODAL_ROOT_ID = "auth-modal-root";
export const authModalRootEl = () => document.getElementById(AUTH_MODAL_ROOT_ID) || document.body;


// El foco teal y el grano de película viven en components/onboarding/fondoMedia.js
// (los comparte con el panel de animación del onboarding, #1525).

// Alto de la animación en el celular (bajo el navbar). El vidrio parte justo debajo.
const ALTO_HISTORIA_MOBILE = 350;

const AuthShell = ({ children }) => {
  const compacto = useMediaQuery("(max-width:899.95px)");
  return (
    <>
    <AlcanceOscuro
        id={AUTH_SCOPE_ID}
        sx={{
          position: "fixed",
          inset: 0,
          overflow: "hidden",
          backgroundColor: "var(--media-bg)",
          color: "var(--text-primary)",
        }}
      >
        <Box aria-hidden="true" sx={{ position: "absolute", inset: 0, pointerEvents: "none", background: { xs: foco("80%", "8%"), md: foco("30%", "22%") } }} />
        <Box aria-hidden="true" sx={{ position: "absolute", inset: 0, pointerEvents: "none", backgroundImage: GRANO, mixBlendMode: "overlay", zIndex: 1 }} />

        {/* La animación: a la izquierda en escritorio, arriba en el celular. */}
        <Box
          sx={{
            position: "absolute",
            zIndex: 2,
            left: 0,
            // Con el banner de mantenimiento visible (#1757 v2) la animación baja lo que él mide
            // (`--banner-mantenimiento-h`, alto + 8 px, ausente = 0px), como en el mockup C: en xs Y desde md.
            // En escritorio la 3ª diapositiva («Cierra tus evoluciones…», ~11 s) tiene chips y un encabezado de
            // vista previa a y 124-146 que el banner (y 76-141) cruzaba ~17 px (F6 del revisor).
            top: {
              xs: "calc(64px + var(--banner-mantenimiento-h, 0px))",
              md: "calc(72px + var(--banner-mantenimiento-h, 0px))",
            },
            right: { xs: 0, md: "min(640px, 46vw)" },
            height: { xs: ALTO_HISTORIA_MOBILE, md: "auto" },
            bottom: { md: 0 },
          }}
        >
          <AuthStory compacto={compacto} />
        </Box>

        {/* El formulario en vidrio. En el celular la columna ocupa toda la
            pantalla y deja libre la animación con un padding superior. */}
        <Box
          sx={{
            position: "absolute",
            zIndex: 3,
            top: 0,
            bottom: 0,
            right: 0,
            left: { xs: 0, md: "auto" },
            width: { md: "min(640px, 46vw)" },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            px: { xs: 1, md: 4 },
            // md: 88 px de siempre; con el banner de mantenimiento visible (#1757 v2, opción C) baja a
            // 148 px, como el `.scr.conban .col` del mockup, para que el banner no tape las pestañas del
            // formulario. Lo fija `BannerMantenimiento` en :root; 88px por defecto (sin banner nada se mueve). NO es un token de tema: AlcanceOscuro reescribe todos los de tokens.dark en su subárbol y fijaría 88px aunque el banner ponga 148px.
            pt: {
              xs: `calc(${64 + ALTO_HISTORIA_MOBILE + 8}px + var(--banner-mantenimiento-h, 0px))`,
              md: "var(--acceso-pt-mantenimiento, 88px)",
            },
            pb: { xs: 1, md: 6 },
            overflowY: "auto",
          }}
        >
          <Box
            sx={{
              my: { md: "auto" },
              width: "100%",
              maxWidth: { xs: "none", md: 500 },
              borderRadius: "30px",
              px: { xs: 2.25, sm: 3, md: 4.25 },
              py: { xs: 2.75, md: 4 },
              backgroundColor: "rgba(var(--shadow-rgb),0.5)",
              border: "1px solid rgba(var(--fg-rgb),0.16)",
              backdropFilter: "blur(18px)",
              WebkitBackdropFilter: "blur(18px)",
              boxShadow: "0 40px 80px -30px rgba(var(--shadow-rgb),0.7)",
              display: "flex",
              justifyContent: "center",
            }}
          >
            {children}
          </Box>
        </Box>
    </AlcanceOscuro>
    <AlcanceOscuro id={AUTH_MODAL_ROOT_ID} sx={{ position: "fixed", top: 0, left: 0, width: 0, height: 0, zIndex: 1600 }} />
    </>
  );
};

export default AuthShell;
